import { GameState, GamePhase, PlayerAction, SpaceType, PropertySpace, PropertyColor, RailroadSpace, UtilitySpace, OwnedProperty, BoardSpace } from '../shared/types.js';
import { BOARD_SPACES } from '../shared/boardSpaces.js';
import { $, show, hide, formatMoney } from './utils.js';
import { openTradeModal } from './tradeModal.js';
import { sendAction } from './state.js';

const COLOR_GROUP_ORDER: { key: string; label: string }[] = [
  { key: PropertyColor.BROWN, label: 'Brown' },
  { key: PropertyColor.LIGHT_BLUE, label: 'Light Blue' },
  { key: PropertyColor.PINK, label: 'Pink' },
  { key: PropertyColor.ORANGE, label: 'Orange' },
  { key: PropertyColor.RED, label: 'Red' },
  { key: PropertyColor.YELLOW, label: 'Yellow' },
  { key: PropertyColor.GREEN, label: 'Green' },
  { key: PropertyColor.DARK_BLUE, label: 'Dark Blue' },
  { key: 'RAILROAD', label: 'Railroads' },
  { key: 'UTILITY', label: 'Utilities' },
];

const ACTION_LABELS: Record<PlayerAction, string> = {
  ROLL_DICE: 'Roll Dice',
  BUY_PROPERTY: 'Buy',
  DECLINE_PROPERTY: 'Decline',
  BUILD_HOUSE: 'Build',
  SELL_HOUSE: 'Sell House',
  PAY_JAIL_FINE: 'Pay $50 Fine',
  USE_JAIL_CARD: 'Use Jail Card',
  ROLL_FOR_DOUBLES: 'Roll for Doubles',
  END_TURN: 'End Turn',
  OFFER_TRADE: 'Trade',
  ACCEPT_TRADE: 'Accept',
  REJECT_TRADE: 'Reject',
  CANCEL_TRADE: 'Cancel Trade',
  MORTGAGE_PROPERTY: 'Mortgage',
  UNMORTGAGE_PROPERTY: 'Unmortgage',
  DECLARE_BANKRUPTCY: 'Declare Bankruptcy',
};

const ACTION_STYLES: Record<PlayerAction, string> = {
  ROLL_DICE: 'btn-primary',
  BUY_PROPERTY: 'btn-success',
  DECLINE_PROPERTY: 'btn-secondary',
  BUILD_HOUSE: 'btn-warning',
  SELL_HOUSE: 'btn-secondary',
  PAY_JAIL_FINE: 'btn-warning',
  USE_JAIL_CARD: 'btn-success',
  ROLL_FOR_DOUBLES: 'btn-primary',
  END_TURN: 'btn-secondary',
  OFFER_TRADE: 'btn-warning',
  ACCEPT_TRADE: 'btn-success',
  REJECT_TRADE: 'btn-primary',
  CANCEL_TRADE: 'btn-secondary',
  MORTGAGE_PROPERTY: 'btn-warning',
  UNMORTGAGE_PROPERTY: 'btn-success',
  DECLARE_BANKRUPTCY: 'btn-danger',
};

let activePropertyMode: PlayerAction | null = null;
let selectedPropertyPos: number | null = null;
let lastBuildPropertyPos: number | null = null;
let canRepeatBuild = false;
let lastFingerprint: string | null = null;
let lastDropdownFp: string | null = null;
let latestGameState: GameState | null = null;
let latestPlayerId = '';
let lastPressAction = '';
let lastPressAt = 0;

// Structural fingerprint: which buttons/messages exist at all. Only these
// inputs force a destroy-and-rebuild of the panel DOM. Money, house counts
// and mortgage status are deliberately EXCLUDED — those change on every
// build/buy and are applied in-place by refreshAttributes() so the buttons
// (and the user's pointer target) survive bursts of state updates.
function fingerprintStructure(state: GameState, myPlayerId: string): string {
  const me = state.players.find(p => p.id === myPlayerId);
  return [
    state.phase,
    state.currentPlayerIndex,
    state.availableActions.join(','),
    state.pendingTrade?.fromPlayerId ?? '',
    state.pendingTrade?.toPlayerId ?? '',
    activePropertyMode ?? '',
    me && me.properties.length > 0 ? 'props' : 'noprops',
  ].join('|');
}

// Dropdown fingerprint: which options are eligible in the property select.
// Repopulating the select doesn't destroy the action buttons, so this can
// change more often than the structure without causing flicker.
function fingerprintDropdown(state: GameState, myPlayerId: string): string {
  const me = state.players.find(p => p.id === myPlayerId);
  return [
    me ? me.properties.join(',') : '',
    me ? me.money : 0,
    activePropertyMode ?? '',
    state.properties.map(p => `${p.position}:${p.isMortgaged ? 'M' : p.houses}`).join(','),
  ].join('|');
}

export function initActionPanel(): void {
  const select = $('#build-property-select') as HTMLSelectElement;
  select.addEventListener('change', () => {
    if (!activePropertyMode) return;
    const pos = parseInt(select.value, 10);
    if (isNaN(pos)) return;
    selectedPropertyPos = pos;
    if (activePropertyMode === 'BUILD_HOUSE') {
      lastBuildPropertyPos = pos;
      canRepeatBuild = true;
    }
    sendAction({ action: activePropertyMode, propertyPosition: pos });
    activePropertyMode = null;
    selectedPropertyPos = null;
  });

  // Stable parent listeners. Buttons are rebuilt on every phase change; if the
  // listener lived on the button, a state packet between mousedown and mouseup
  // ate the click and the roll/end-turn looked like it needed 2–3 presses.
  const container = $('#action-buttons');
  container.addEventListener('pointerdown', (ev: PointerEvent) => {
    if (ev.button !== 0) return;
    const btn = targetActionButton(ev);
    if (!btn) return;
    pressActionButton(btn);
  });
  container.addEventListener('click', (ev: MouseEvent) => {
    const btn = targetActionButton(ev);
    if (btn) pressActionButton(btn);
  });
}

function targetActionButton(ev: Event): HTMLButtonElement | null {
  const el = ev.target;
  if (!(el instanceof Element)) return null;
  const btn = el.closest('button[data-action]');
  if (!(btn instanceof HTMLButtonElement) || btn.disabled) return null;
  return btn;
}

function pressActionButton(btn: HTMLButtonElement): void {
  const action = btn.dataset.action as PlayerAction | undefined;
  if (!action) return;
  const now = performance.now();
  if (action === lastPressAction && now - lastPressAt < 180) return;
  lastPressAction = action;
  lastPressAt = now;

  const state = latestGameState;
  const myPlayerId = latestPlayerId;

  if (action === 'BUILD_HOUSE' || action === 'SELL_HOUSE'
    || action === 'MORTGAGE_PROPERTY' || action === 'UNMORTGAGE_PROPERTY') {
    if (!state) return;
    if (action === 'BUILD_HOUSE') {
      if (canRepeatBuild && lastBuildPropertyPos !== null) {
        const target = resolveBuildTarget(state, myPlayerId);
        if (target !== null) {
          lastBuildPropertyPos = target;
          syncBuildDropdown(target);
          sendAction({ action: 'BUILD_HOUSE', propertyPosition: target });
          canRepeatBuild = true;
          return;
        }
      }
      const propertyButtons = Array.from(
        $('#action-buttons').querySelectorAll('button[data-action]'),
      ) as HTMLButtonElement[];
      enterPropertyPickMode(btn, action, state, myPlayerId, propertyButtons);
      return;
    }
    const propertyButtons = Array.from(
      $('#action-buttons').querySelectorAll('button[data-action]'),
    ) as HTMLButtonElement[];
    enterPropertyPickMode(btn, action, state, myPlayerId, propertyButtons);
    return;
  }

  if (action === 'OFFER_TRADE') {
    if (state) openTradeModal(state, myPlayerId);
    return;
  }

  // The dice spin for exactly as long as the roll is unanswered. Starting the
  // animation before the roll is sent stranded it there when the click turned
  // out to be a duplicate: nothing owned the animation, so nothing stopped it.
  const isRoll = action === 'ROLL_DICE' || action === 'ROLL_FOR_DOUBLES';
  if (!sendAction({ action }, isRoll ? () => spinDice(false) : undefined)) return;
  if (isRoll) spinDice(true);
  btn.disabled = true;
}

function spinDice(on: boolean): void {
  document.getElementById('die-1')?.classList.toggle('rolling', on);
  document.getElementById('die-2')?.classList.toggle('rolling', on);
}

export function updateActions(state: GameState, myPlayerId: string): void {
  latestGameState = state;
  latestPlayerId = myPlayerId;

  const currentPlayerForTurn = state.players[state.currentPlayerIndex];
  const isMyBuildTurn = currentPlayerForTurn?.id === myPlayerId
    && state.phase === GamePhase.TURN_END
    && state.availableActions.includes('BUILD_HOUSE');
  if (!isMyBuildTurn) {
    canRepeatBuild = false;
  }

  const structureFp = fingerprintStructure(state, myPlayerId);
  if (structureFp !== lastFingerprint) {
    lastFingerprint = structureFp;
    rebuildStructure(state, myPlayerId);
  }
  // In-place attribute pass: runs on EVERY state, touches existing nodes only.
  refreshAttributes(state, myPlayerId);
}

// Destroy-and-rebuild of the action panel. Called only when the set of
// visible buttons actually changes (turn/phase/action-list transitions).
function rebuildStructure(state: GameState, myPlayerId: string): void {
  const container = $('#action-buttons');
  const buildSelector = $('#build-selector');
  container.innerHTML = '';

  const me = state.players.find(p => p.id === myPlayerId);
  const currentPlayer = state.players[state.currentPlayerIndex];

  // Always show the property dropdown if the player owns properties
  if (me && me.properties.length > 0) {
    show(buildSelector);
    if (activePropertyMode && state.availableActions.includes(activePropertyMode)) {
      populateFilteredProperties(state, myPlayerId, activePropertyMode);
      restoreSelection();
    } else {
      activePropertyMode = null;
      selectedPropertyPos = null;
      populateAllProperties(state, myPlayerId);
    }
    lastDropdownFp = fingerprintDropdown(state, myPlayerId);
  } else {
    hide(buildSelector);
    activePropertyMode = null;
    selectedPropertyPos = null;
    lastDropdownFp = null;
  }

  // During TRADING the target must be able to answer from this panel.
  // The incoming modal is easy to miss, and without these buttons the
  // panel is blank while the turn still belongs to the NPC who offered.
  if (state.phase === GamePhase.TRADING && state.pendingTrade) {
    if (myPlayerId === state.pendingTrade.toPlayerId) {
      for (const action of ['ACCEPT_TRADE', 'REJECT_TRADE'] as const) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = `btn ${ACTION_STYLES[action]}`;
        btn.textContent = ACTION_LABELS[action];
        btn.dataset.action = action;
        container.appendChild(btn);
      }
      return;
    }
    if (myPlayerId === state.pendingTrade.fromPlayerId) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `btn ${ACTION_STYLES['CANCEL_TRADE']}`;
      btn.textContent = ACTION_LABELS['CANCEL_TRADE'];
      btn.dataset.action = 'CANCEL_TRADE';
      container.appendChild(btn);
      return;
    }
    const waitMsg = document.createElement('div');
    waitMsg.textContent = 'A trade is in progress...';
    waitMsg.style.color = '#aaa';
    waitMsg.style.fontStyle = 'italic';
    container.appendChild(waitMsg);
    return;
  }

  if (!currentPlayer || currentPlayer.id !== myPlayerId) {
    const waitMsg = document.createElement('div');
    waitMsg.textContent = `Waiting for ${currentPlayer?.name || '...'}`;
    waitMsg.style.color = '#aaa';
    waitMsg.style.fontStyle = 'italic';
    container.appendChild(waitMsg);
    return;
  }

  for (const action of state.availableActions) {
    if (action === 'BUILD_HOUSE' || action === 'SELL_HOUSE'
      || action === 'MORTGAGE_PROPERTY' || action === 'UNMORTGAGE_PROPERTY') {

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `btn ${ACTION_STYLES[action]}`;
      btn.textContent = ACTION_LABELS[action];
      btn.dataset.action = action;
      if (activePropertyMode === action) btn.classList.add('active-mode');
      if (action === 'BUILD_HOUSE' && !hasAffordableBuild(state, myPlayerId)) {
        btn.disabled = true;
        btn.title = 'Not enough money — mortgage or sell houses first';
      }
      container.appendChild(btn);
      continue;
    }

    if (action === 'OFFER_TRADE') {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `btn ${ACTION_STYLES[action]}`;
      btn.textContent = ACTION_LABELS[action];
      btn.dataset.action = action;
      container.appendChild(btn);
      continue;
    }

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `btn ${ACTION_STYLES[action]}`;
    btn.dataset.action = action;
    btn.textContent = ACTION_LABELS[action];
    container.appendChild(btn);
  }
}

// In-place pass: updates prices, affordability and enabled/disabled flags on
// existing buttons without rebuilding them. Also repopulates the property
// dropdown when its eligible set changed. Runs on every state arrival —
// O(buttons), no DOM churn, no flicker.
function refreshAttributes(state: GameState, myPlayerId: string): void {
  const container = $('#action-buttons');
  const me = state.players.find(p => p.id === myPlayerId);
  const currentPlayer = state.players[state.currentPlayerIndex];
  const isMyTurn = currentPlayer?.id === myPlayerId;

  for (const el of Array.from(container.querySelectorAll('button'))) {
    const btn = el as HTMLButtonElement;
    const action = btn.dataset.action as PlayerAction | undefined;
    if (!action) continue;

    // Default: enabled (also clears the temporary disable from a click).
    btn.disabled = false;
    btn.title = '';

    if (action === 'BUILD_HOUSE') {
      if (!hasAffordableBuild(state, myPlayerId)) {
        btn.disabled = true;
        btn.title = 'Not enough money — mortgage or sell houses first';
      }
    } else if (action === 'BUY_PROPERTY' && isMyTurn && currentPlayer) {
      const space = BOARD_SPACES[currentPlayer.position];
      const price = (space as PropertySpace | RailroadSpace | UtilitySpace).price;
      const label = `${ACTION_LABELS[action]} ${formatMoney(price)}`;
      if (btn.textContent !== label) btn.textContent = label;
      if (currentPlayer.money < price) {
        btn.disabled = true;
        btn.title = 'Not enough money — mortgage or sell houses first';
      }
    }
  }

  // Property dropdown options: refresh only when eligibility changed.
  if (me && me.properties.length > 0) {
    const fp = fingerprintDropdown(state, myPlayerId);
    if (fp !== lastDropdownFp) {
      lastDropdownFp = fp;
      if (activePropertyMode && state.availableActions.includes(activePropertyMode)) {
        populateFilteredProperties(state, myPlayerId, activePropertyMode);
        restoreSelection();
      } else {
        populateAllProperties(state, myPlayerId);
      }
    }
  }
}

function enterPropertyPickMode(
  btn: HTMLButtonElement,
  action: PlayerAction,
  state: GameState,
  myPlayerId: string,
  propertyButtons: HTMLButtonElement[],
): void {
  activePropertyMode = action;
  populateFilteredProperties(state, myPlayerId, action);
  for (const b of propertyButtons) {
    b.classList.remove('active-mode');
    b.textContent = ACTION_LABELS[b.dataset.action as PlayerAction] ?? b.textContent;
  }
  btn.classList.add('active-mode');
  btn.textContent = `${ACTION_LABELS[action]} ↑ pick one`;
  const select = $('#build-property-select') as HTMLSelectElement;
  const placeholder = document.createElement('option');
  placeholder.value = '';
  placeholder.textContent = '— select a property —';
  placeholder.disabled = true;
  placeholder.selected = true;
  select.insertBefore(placeholder, select.firstChild);
}

function hasAffordableBuild(state: GameState, myPlayerId: string): boolean {
  const player = state.players.find(p => p.id === myPlayerId);
  if (!player) return false;
  for (const prop of state.properties) {
    if (prop.ownerId !== player.id) continue;
    if (canBuildClient(player.id, prop.position, state.properties)
      && canAffordBuild(prop.position, player.money)) {
      return true;
    }
  }
  return false;
}

function restoreSelection(): void {
  if (selectedPropertyPos === null) return;
  const select = $('#build-property-select') as HTMLSelectElement;
  const val = String(selectedPropertyPos);
  // If the previously selected option still exists, restore it
  for (const opt of Array.from(select.options)) {
    if (opt.value === val) {
      select.value = val;
      return;
    }
  }
  // Property no longer in list (e.g. it was just mortgaged), clear selection
  selectedPropertyPos = null;
}

function getPropertyGroupKey(space: BoardSpace): string {
  if (space.type === SpaceType.PROPERTY) return (space as PropertySpace).color;
  if (space.type === SpaceType.RAILROAD) return 'RAILROAD';
  if (space.type === SpaceType.UTILITY) return 'UTILITY';
  return '';
}

function getStatusIcon(owned: OwnedProperty): string {
  if (owned.isMortgaged) return '\u24C2';  // Ⓜ
  if (owned.houses === 5) return '\u{1F3E8}'; // 🏨
  if (owned.houses > 0) return `${owned.houses}\u2302`; // e.g. "2⌂"
  return '';
}

function getColorGroupPositions(position: number): number[] {
  const space = BOARD_SPACES[position];
  if (space.type !== SpaceType.PROPERTY) return [];
  const color = (space as PropertySpace).color;
  return BOARD_SPACES
    .filter((s): s is PropertySpace => s.type === SpaceType.PROPERTY && s.color === color)
    .map(s => s.position)
    .sort((a, b) => a - b);
}

function canAffordBuild(position: number, money: number): boolean {
  const space = BOARD_SPACES[position];
  if (space.type !== SpaceType.PROPERTY) return false;
  return money >= (space as PropertySpace).houseCost;
}

/** Next build target: repeat on last property, or next in the color group when it has a hotel. */
function resolveBuildTarget(state: GameState, myPlayerId: string): number | null {
  const player = state.players.find(p => p.id === myPlayerId);
  if (!player) return null;

  if (lastBuildPropertyPos !== null) {
    const fromLast = nextBuildFromLast(lastBuildPropertyPos, player.id, player.money, state.properties);
    if (fromLast !== null) return fromLast;
  }

  return firstBuildableProperty(player.id, player.money, state.properties);
}

function nextBuildFromLast(
  lastPos: number,
  ownerId: string,
  money: number,
  allProperties: OwnedProperty[],
): number | null {
  const owned = allProperties.find(p => p.position === lastPos);

  if (owned && owned.houses < 5 && canBuildClient(ownerId, lastPos, allProperties)) {
    return canAffordBuild(lastPos, money) ? lastPos : null;
  }

  const group = getColorGroupPositions(lastPos);
  const startIdx = group.indexOf(lastPos);
  if (startIdx === -1) return null;

  for (let i = 1; i < group.length; i++) {
    const pos = group[(startIdx + i) % group.length];
    if (canBuildClient(ownerId, pos, allProperties) && canAffordBuild(pos, money)) {
      return pos;
    }
  }
  return null;
}

function firstBuildableProperty(
  ownerId: string,
  money: number,
  allProperties: OwnedProperty[],
): number | null {
  const buildable: { groupOrder: number; position: number }[] = [];
  for (const prop of allProperties) {
    if (prop.ownerId !== ownerId) continue;
    if (!canBuildClient(ownerId, prop.position, allProperties)) continue;
    if (!canAffordBuild(prop.position, money)) continue;
    const space = BOARD_SPACES[prop.position];
    const key = getPropertyGroupKey(space);
    const groupOrder = COLOR_GROUP_ORDER.findIndex(g => g.key === key);
    buildable.push({ groupOrder: groupOrder === -1 ? 999 : groupOrder, position: prop.position });
  }
  if (buildable.length === 0) return null;
  buildable.sort((a, b) => a.groupOrder - b.groupOrder || a.position - b.position);
  return buildable[0].position;
}

function syncBuildDropdown(position: number): void {
  const select = $('#build-property-select') as HTMLSelectElement;
  const val = String(position);
  for (const opt of Array.from(select.options)) {
    if (opt.value === val) {
      select.value = val;
      return;
    }
  }
}

function canBuildClient(ownerId: string, position: number, allProperties: OwnedProperty[]): boolean {
  const space = BOARD_SPACES[position];
  if (space.type !== SpaceType.PROPERTY) return false;
  const propSpace = space as PropertySpace;

  const owned = allProperties.find(p => p.position === position);
  if (!owned || owned.ownerId !== ownerId || owned.isMortgaged) return false;
  if (owned.houses >= 5) return false;

  // Check monopoly
  const colorPositions = BOARD_SPACES
    .filter((s): s is PropertySpace => s.type === SpaceType.PROPERTY && s.color === propSpace.color)
    .map(s => s.position);
  const hasMonopoly = colorPositions.every(pos =>
    allProperties.some(p => p.position === pos && p.ownerId === ownerId && !p.isMortgaged)
  );
  if (!hasMonopoly) return false;

  return true;
}

function canSellClient(ownerId: string, position: number, allProperties: OwnedProperty[]): boolean {
  const space = BOARD_SPACES[position];
  if (space.type !== SpaceType.PROPERTY) return false;

  const owned = allProperties.find(p => p.position === position);
  if (!owned || owned.ownerId !== ownerId || owned.houses === 0) return false;

  return true;
}

function getFilteredLabel(space: BoardSpace, owned: OwnedProperty, mode: PlayerAction, ownerId: string, allProperties: OwnedProperty[]): string | null {
  if (mode === 'MORTGAGE_PROPERTY') {
    if (owned.isMortgaged || owned.houses > 0) return null;
    if (space.type !== SpaceType.PROPERTY && space.type !== SpaceType.RAILROAD && space.type !== SpaceType.UTILITY) return null;
    const mortgageValue = (space as PropertySpace | RailroadSpace | UtilitySpace).mortgageValue;
    return `${space.name} +${formatMoney(mortgageValue)}`;
  } else if (mode === 'UNMORTGAGE_PROPERTY') {
    if (!owned.isMortgaged) return null;
    if (space.type !== SpaceType.PROPERTY && space.type !== SpaceType.RAILROAD && space.type !== SpaceType.UTILITY) return null;
    const mortgageValue = (space as PropertySpace | RailroadSpace | UtilitySpace).mortgageValue;
    const cost = Math.round(mortgageValue * 1.1);
    return `${space.name} ${formatMoney(cost)}`;
  } else if (mode === 'SELL_HOUSE') {
    if (!canSellClient(ownerId, space.position, allProperties)) return null;
    const propSpace = space as PropertySpace;
    return `${space.name} (${owned.houses === 5 ? 'Hotel' : owned.houses + 'H'}) +${formatMoney(Math.floor(propSpace.houseCost / 2))}`;
  } else {
    // BUILD_HOUSE
    if (!canBuildClient(ownerId, space.position, allProperties)) return null;
    const propSpace = space as PropertySpace;
    return `${space.name} (${owned.houses === 5 ? 'Hotel' : owned.houses + 'H'}) ${formatMoney(propSpace.houseCost)}`;
  }
}

/** Show all owned properties with status icons (default view) */
function populateAllProperties(state: GameState, myPlayerId: string): void {
  const select = $('#build-property-select') as HTMLSelectElement;
  select.innerHTML = '';

  const player = state.players.find(p => p.id === myPlayerId);
  if (!player) return;

  // Group by color
  const grouped = new Map<string, { pos: number; label: string }[]>();

  for (const pos of player.properties) {
    const space = BOARD_SPACES[pos];
    const owned = state.properties.find(p => p.position === pos);
    if (!owned) continue;

    const icon = getStatusIcon(owned);
    const prefix = icon ? `${icon} ` : '';
    const label = `${prefix}${space.name}`;

    const key = getPropertyGroupKey(space);
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push({ pos, label });
  }

  renderGroupedOptions(select, grouped);
}

/** Show only properties eligible for a specific action (filtered view) */
function populateFilteredProperties(state: GameState, myPlayerId: string, mode: PlayerAction): void {
  const select = $('#build-property-select') as HTMLSelectElement;
  select.innerHTML = '';

  const player = state.players.find(p => p.id === myPlayerId);
  if (!player) return;

  // For mortgage mode, pre-compute which color groups have houses
  const groupsWithHouses = new Set<string>();
  if (mode === 'MORTGAGE_PROPERTY') {
    for (const prop of state.properties) {
      if (prop.houses > 0) {
        const s = BOARD_SPACES[prop.position];
        if (s.type === SpaceType.PROPERTY) {
          groupsWithHouses.add((s as PropertySpace).color);
        }
      }
    }
  }

  const grouped = new Map<string, { pos: number; label: string }[]>();

  for (const pos of player.properties) {
    const space = BOARD_SPACES[pos];
    const owned = state.properties.find(p => p.position === pos);
    if (!owned) continue;

    // Skip mortgage-ineligible color groups
    if (mode === 'MORTGAGE_PROPERTY' && space.type === SpaceType.PROPERTY) {
      if (groupsWithHouses.has((space as PropertySpace).color)) continue;
    }

    // Check unmortgage affordability
    if (mode === 'UNMORTGAGE_PROPERTY') {
      const mortgageValue = (space as PropertySpace | RailroadSpace | UtilitySpace).mortgageValue;
      const cost = Math.round(mortgageValue * 1.1);
      if (player.money < cost) continue;
    }

    const label = getFilteredLabel(space, owned, mode, player.id, state.properties);
    if (!label) continue;

    const key = getPropertyGroupKey(space);
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push({ pos, label });
  }

  renderGroupedOptions(select, grouped);
}

function renderGroupedOptions(select: HTMLSelectElement, grouped: Map<string, { pos: number; label: string }[]>): void {
  for (const group of COLOR_GROUP_ORDER) {
    const items = grouped.get(group.key);
    if (!items || items.length === 0) continue;

    const optgroup = document.createElement('optgroup');
    optgroup.label = group.label;

    for (const item of items) {
      const option = document.createElement('option');
      option.value = String(item.pos);
      option.textContent = item.label;
      optgroup.appendChild(option);
    }

    select.appendChild(optgroup);
  }
}
